import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { useParams } from "react-router-dom";
import SingleCard from "../AdsPage/SingleCard";
import Pagination from "../Pagination"; // Assuming custom pagination
import LoadingAnimation from "../Animations/LoadingAnimation";
import {
  cardHover,
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
  notFoundBox,
} from "../../Styles/Styles";

const ProfilePage = () => {
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [adsData, setAdsData] = useState([]);
  const [totalAds, setTotalAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [totalPages, setTotalPages] = useState(1);
  const adsPerPage = 12; // Define the number of ads per page

  const fetchUserData = async () => {
    if (!id) {
      console.error("User ID is undefined.");
      return;
    }

    try {
      console.log(`Fetching page: ${currentPage}`); // Debugging the current page
      // Send correct page number in query params
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/profile/${id}?page=${currentPage}&limit=${adsPerPage}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch user data: ${response.statusText}`);
      }
      const data = await response.json();
      setUserData(data.user);
      setAdsData(data.ads.docs); // Assuming server sends ads in a `docs` field
      setTotalPages(data.ads.totalPages); // Assuming server sends total pages
      setTotalAds(data.ads.totalDocs);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchUserData(); // Fetch data when currentPage changes
  }, [id, currentPage]); // Re-fetch data when page or ID changes

  const handlePageChange = (newPage) => {
    console.log(`Page changed to: ${newPage}`); // Debugging page change
    setCurrentPage(newPage); // Update current page
  };

  if (!userData) {
    return <LoadingAnimation />;
  }

  return (
    <Box
      sx={{
        p: 4,
        minHeight: "100vh",
      }}
    >
      <Grid container spacing={4}>
        {/* User Info Section */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: "white",
              borderRadius: 2,
              ...cardOutline,
            }}
          >
            <Avatar
              src={
                userData.image ||
                "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
              }
              alt={userData.firstName}
              sx={{
                width: 100,
                height: 100,
                mx: "auto",
                mb: 2,
                fontSize: 32,
                color: "white",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ ...headingFonts }}>
                {userData.firstName} {userData.lastName}
              </Typography>
              {userData.isVerifiedbyID === "approved" && (
                <Tooltip
                  title="This user's identity has been verified through a government ID check."
                  arrow
                >
                  <IconButton
                    size="small"
                    sx={{ color: "green", ...noOutline }}
                  >
                    <VerifiedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Typography variant="body1" gutterBottom sx={{ ...fonts }}>
              @{userData.username}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              gutterBottom
              sx={{ ...fonts }}
            >
              Ads Published: <strong>{totalAds || 0}</strong>
            </Typography>
          </Box>
        </Grid>

        {/* Ads List Section */}
        <Grid item xs={12} md={9}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
              Published Ads
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {adsData && adsData.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {adsData.map((AdInfo, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <SingleCard AdInfo={AdInfo} />
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{ mt: 4, display: "flex", justifyContent: "center" }}
                  >
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ ...notFoundBox }}>
                <SearchOffOutlinedIcon
                  sx={{
                    color: "var(--primary-color)",
                    fontSize: 50,
                    opacity: 0.5,
                    marginBottom: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--primary-color)",
                    ...headingFonts,
                  }}
                >
                  No ads.
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, ...fonts }}>
                  User didn't published any Ads.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
